/**
 * Confidence scoring and flagging utilities.
 * Mirrors the logic in the vanilla JS frontend (app.js).
 */

export type ConfidenceLevel = 'high' | 'medium' | 'low' | 'none'

/**
 * Returns the effective confidence score, accounting for verified items.
 * Verified items always have confidence 1.0 regardless of the stored value.
 */
export function effectiveConfidence(
  confidence: number | null | undefined,
  qualityScore: string | null | undefined,
): number | null {
  if (qualityScore === 'verified') return 1.0
  return confidence ?? null
}

/**
 * Classifies a confidence score into a display level.
 */
export function confidenceLevel(confidence: number | null | undefined): ConfidenceLevel {
  if (confidence == null) return 'none'
  if (confidence >= 0.9) return 'high'
  if (confidence >= 0.75) return 'medium'
  return 'low'
}

/**
 * Returns the CSS variable class for a confidence level.
 */
export function confidenceColorClass(level: ConfidenceLevel): string {
  const map: Record<ConfidenceLevel, string> = {
    high: 'text-[hsl(var(--conf-high))]',
    medium: 'text-[hsl(var(--conf-med))]',
    low: 'text-[hsl(var(--conf-low))]',
    none: 'text-[hsl(var(--conf-none))]',
  }
  return map[level]
}

/**
 * Returns the background CSS class for a confidence level (used in badges).
 */
export function confidenceBgClass(level: ConfidenceLevel): string {
  const map: Record<ConfidenceLevel, string> = {
    high: 'bg-[hsl(var(--conf-high))]',
    medium: 'bg-[hsl(var(--conf-med))]',
    low: 'bg-[hsl(var(--conf-low))]',
    none: 'bg-[hsl(var(--conf-none))]',
  }
  return map[level]
}

/**
 * Determines if an entity is flagged (needs attention).
 * Flagged means: low confidence, or name doesn't match Wikidata label.
 * Verified items are never flagged.
 */
export function isFlagged(
  name: string | null | undefined,
  label: string | null | undefined,
  confidence: number | null | undefined,
  qualityScore: string | null | undefined,
): boolean {
  if (qualityScore === 'verified') return false
  if (confidence != null && confidence < 0.75) return true
  if (label && name) {
    const words = name.toLowerCase().split(/\s+/)
    const lbl = label.toLowerCase()
    if (words.filter((w) => w.length > 2 && lbl.includes(w)).length === 0) return true
  }
  return false
}

/**
 * Determines if an entity is effectively matched to Wikidata.
 */
export function isEffectivelyMatched(item: {
  quality_score?: string | null
  wikidata_id?: string | null
}): boolean {
  return item.quality_score === 'verified' || !!item.wikidata_id
}

export interface ConfidenceBuckets {
  high: number // 0.90+
  medium: number // 0.75-0.89
  low: number // <0.75
  unmatched: number // no match
}

/**
 * Computes confidence distribution buckets for a list of entities.
 */
export function computeConfidenceBuckets(
  items: Array<{
    confidence?: number | null
    quality_score?: string | null
    wikidata_id?: string | null
  }>,
): ConfidenceBuckets {
  const buckets: ConfidenceBuckets = { high: 0, medium: 0, low: 0, unmatched: 0 }
  for (const item of items) {
    const c = effectiveConfidence(item.confidence, item.quality_score)
    if (c == null && !item.wikidata_id) {
      buckets.unmatched++
    } else if (c != null && c >= 0.9) {
      buckets.high++
    } else if (c != null && c >= 0.75) {
      buckets.medium++
    } else {
      buckets.low++
    }
  }
  return buckets
}
